import NetInfo from '@react-native-community/netinfo';
import Lang from 'assets/Lang';
import axios from 'axios';
import DropAlert from 'common/components/views/DropAlert';
import FirebaseService from 'common/services/FirebaseService';
import NavigationService from 'common/services/NavigationService';
import StorageService from 'common/services/StorageService';
import ApiConst from 'consts/ApiConst';
import AppConst from 'consts/AppConst';
import ScreenConst from 'consts/ScreenConst';
import StorageConst from 'consts/StorageConst';
import UrlConst from 'consts/UrlConst';
import moment from 'moment';
import { Alert, Platform } from 'react-native';
import fs from 'react-native-fs';
import Compress from 'react-native-compressor';
import RNHeicConverter from 'react-native-heic-converter';
import HomeActionCreator from 'states/redux/actionCreators/HomeActionCreator';
import Configs from 'utils/Configs';
import ApiHost from './ApiHost';
import File from './File';
import Funcs from './Funcs';
import Strings from './Strings';

const Status = {
  MAINTENANCE: 503,
  INTERNAL_SERVER_ERROR: 500,
  NOT_EXITS: 400,
  UNAUTHORIZED: 401,
  SUCCESS: 200,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  ALREADY_EXIST: 409,
  PHONE_DOES_NOT_EXIT: 426,
  CODE_DOES_NOT_EXIT: 427,
  PHONE_ALREADY_LOGIN_SOCIAL: 428,
  POINT_NOT_ENOUGH: 429,
  DATE_EXPIRY: 434,
  NUMBER_EXPIRED: 432,
  NOT_ENTER_YOUR_CODE: 430,
  ERROR_MULTI_CODE: 435,
  GENERIC_ERROR: 1000,
  NETWORK_ERROR: 1001,
  TIMEOUT: 408
};

let baseURL = UrlConst.API;
let instance = axios.create({ baseURL, withCredentials: true, timeout: 15000 });
// Request có file (upload ảnh) trên mạng yếu cần lâu hơn API thường, timeout 15s chung hay bị cắt giữa chừng (lỗi 408)
const UPLOAD_TIMEOUT = 60000;
ApiHost.bind(instance);

const genHeader = (auth) => {
  let headers = {};
  headers.warehouseid = Configs.warehouse?.id;
  headers.platform = Platform.OS;
  headers.language = Configs.language;
  headers.version = AppConst.VERSION;
  headers.timezone = Configs.timezone;
  headers.country = Configs.countryCode;
  if (typeof auth === 'object') {
    headers = { ...headers, ...auth, token: Configs.token || Configs.user?.token };
    if (auth.auth === false) headers.token = null;
  } else if (auth) {
    headers = { ...headers, token: Configs.token || Configs.user?.token };
  }
  if (!headers.token) headers.token = '';
  return headers;
};

const isTimeoutError = (err) => err?.code === 'ECONNABORTED' || err?.code === 'ETIMEDOUT' || err?.message?.toLowerCase().includes('timeout');

// logNetworkError bỏ qua timeout, nhưng upload bị timeout thì request thường chưa tới server nên server không có log.
// Log riêng lên Firebase kèm thời gian, số file và loại mạng để biết user bị chậm ở đâu.
async function logUploadTimeout(err, { url, baseURL: apiHost, timeout, startedAt, fileCount }) {
  if (fileCount < 1 || !isTimeoutError(err)) return;
  try {
    const netState = await NetInfo.fetch();
    const durationMs = Date.now() - startedAt;
    const cellular = netState.details?.cellularGeneration || '';
    const message = [
      err?.code || 'NO_CODE',
      err?.message || 'Timeout',
      `platform=${Platform.OS}`,
      `version=${AppConst.VERSION}`,
      `host=${apiHost}`,
      `timeout=${timeout}`,
      `duration=${durationMs}ms`,
      `files=${fileCount}`,
      `network=${netState.type}`,
      `cellular=${cellular}`,
      `connected=${netState.isConnected}`
    ].join(' | ');
    const exception = new Error(message);
    exception.name = `ApiUploadTimeout_${url}`;
    FirebaseService.logError(exception);
  } catch (netInfoError) {
    Funcs.log('NetInfo error', netInfoError);
  }
}

async function logNetworkError(err, config) {
  try {
    const netState = await NetInfo.fetch();
    const isCurrentlyOffline = netState.isConnected === false || netState.isInternetReachable === false;
    if (isCurrentlyOffline) return;

    const method = config?.method || 'UNKNOWN';
    const url = config?.url || 'unknown';
    const apiHost = config?.baseURL || instance.defaults.baseURL || UrlConst.API;
    const message = `${err?.code || 'NO_CODE'} | ${err?.message || 'Network error'} | platform=${Platform.OS} | version=${AppConst.VERSION} | host=${apiHost} | connected=${
      netState.isConnected
    } | internetReachable=${netState.isInternetReachable}`;
    const isExpectedNetworkError = err?.code === 'ECONNABORTED' || err?.code === 'ETIMEDOUT' || err?.code === 'ERR_NETWORK' || err?.message === 'Network Error';
    if (isExpectedNetworkError) return;

    const exception = new Error(message);
    exception.name = `ApiNetworkError_${method}_${url}`;
    FirebaseService.logError(exception);
  } catch (netInfoError) {
    Funcs.log('NetInfo error', netInfoError);
  }
}

async function parseErrorResult(err, config) {
  // Request bị app tự huỷ, không alert hoặc log global. Caller cần check cancelled.
  if (axios.isCancel(err)) {
    return { config, status: Status.GENERIC_ERROR, message: 'Cancelled', cancelled: true };
  }

  var result = err && err.response;
  if (!result) {
    const isTimeout = isTimeoutError(err);
    result = { config, status: isTimeout ? Status.TIMEOUT : Status.NETWORK_ERROR, message: isTimeout ? 'Timeout' : 'Network error', error: err?.message || err?.error };
    logNetworkError(err, config);
  }

  let isNetworkConnection = true;
  const { headers = {} } = config;
  Funcs.log('ERROR', result);
  if (result.status === Status.NETWORK_ERROR) {
    isNetworkConnection = false;
    if (!headers?.disableAlert) Alert.alert(Lang.maintenance.networkError, Lang.maintenance.networkErrorDetail);
  } else if (result.status === Status.MAINTENANCE) {
    isNetworkConnection = false;
    if (!headers?.disableAlert) Alert.alert(Lang.maintenance.title, Strings.format(Lang.maintenance.message, [result?.data?.time || 10]));
  } else if (result.status === Status.TIMEOUT) {
    if (!headers?.disableAlert) Alert.alert('', Lang.maintenance.timeout);
  } else if (result.status === Status.UNAUTHORIZED && Configs.token && config?.url !== ApiConst.AUTHENTICATE.REFRESH) {
    Configs.token = null;
    Configs.user = {};
    DropAlert.error('', Lang.setting.loginExpired);
    StorageService.remove(StorageConst.TOKEN);
    throw NavigationService.reset(ScreenConst.AUTH_NAVIGATOR);
  }
  HomeActionCreator.updateNetworkState(isNetworkConnection);
  return result;
}

const isStaticURL = (url) => url?.indexOf('http://') === 0 || url?.indexOf('https://') === 0;

const normalizeFile = (value) => {
  if (!value || typeof value !== 'object' || typeof value.uri !== 'string') return value;
  if (Platform.OS === 'android' && value.uri.startsWith('/')) {
    return { ...value, uri: `file://${value.uri}` };
  }
  return value;
};

const normalizeAndroidImageUri = (uri) => normalizeFile({ uri }).uri.replace(/^file:\/\/(?:file:\/\/)+/, 'file://');

async function parseSuccessResult(res) {
  const { url, headers, params, method } = res.config;
  var result = { data: res.data, status: res.status, config: { url, headers, params, method } };
  Funcs.log('SUCCESS', result);
  HomeActionCreator.updateNetworkState(true);
  return result;
}

async function get(url, data, auth = true, cancelRequest) {
  let headers = genHeader(auth);
  try {
    const isStaticUrl = isStaticURL(url);
    const requestBaseURL = isStaticUrl ? '' : instance.defaults.baseURL;
    let fullUrl = isStaticUrl ? url : requestBaseURL + url;
    Funcs.log('GET', fullUrl, data, headers);
    var res = await instance.get(url, {
      params: { ...data },
      ...(headers.timeout > 0 ? { timeout: headers.timeout } : {}),
      headers,
      baseURL: requestBaseURL,
      withCredentials: !isStaticUrl,
      cancelToken: new axios.CancelToken(function executor(c) {
        if (cancelRequest) cancelRequest.cancel = c;
      })
    });
    return parseSuccessResult(res);
  } catch (err) {
    return parseErrorResult(err, { url, data, headers, method: 'GET' });
  }
}

async function post(url, data, auth = true, cancelRequest) {
  let headers = genHeader(auth);
  try {
    const isStaticUrl = isStaticURL(url);
    const requestBaseURL = isStaticUrl ? '' : instance.defaults.baseURL;
    let fullUrl = isStaticUrl ? url : requestBaseURL + url;
    Funcs.log('POST', fullUrl, data, headers);
    var res = await instance.post(
      url,
      { ...data },
      {
        ...(headers.timeout > 0 ? { timeout: headers.timeout } : {}),
        headers,
        baseURL: requestBaseURL,
        withCredentials: !isStaticUrl,
        cancelToken: new axios.CancelToken(function executor(c) {
          if (cancelRequest) cancelRequest.cancel = c;
        })
      }
    );
    return parseSuccessResult(res);
  } catch (err) {
    return parseErrorResult(err, { url, data, headers, method: 'POST' });
  }
}

async function postForm(url, params, auth = true, cancelRequest) {
  const arrayImageConverted = [];
  async function optimizeImage(imgData) {
    if (!imgData?.uri) return imgData;
    try {
      const ingnoreConvertTypes = ['jpg', 'jpeg', 'png', 'image/jpeg', 'image/jpg', 'image/png'];

      // Convert heic (iOS)
      if (AppConst.IS_IOS && ingnoreConvertTypes.indexOf(imgData.type?.toLowerCase()) < 0) {
        Funcs.log('Convert heic', imgData);
        let convertedImage = await Promise.race([
          RNHeicConverter.convert({ path: imgData.uri, quality: 0.9 }),
          new Promise((_, reject) => setTimeout(() => reject('HEIC timeout'), 5000))
        ]);
        if (convertedImage?.path) {
          imgData.uri = convertedImage.path;
          imgData.type = 'image/jpg';
          arrayImageConverted.push(convertedImage.path);
        }
      }

      let imageUri = imgData.uri;
      // Android compressor cannot reliably read content URI from every provider.
      if (AppConst.IS_ANDROID) {
        imageUri = normalizeAndroidImageUri(imageUri);
        if (imageUri.startsWith('content://')) {
          imageUri = await Compress.getRealPath(imageUri, 'image');
          imageUri = normalizeAndroidImageUri(imageUri);
        }
      }

      // iOS native compressor throws NSException for a missing or unreadable local file.
      if (AppConst.IS_IOS && !imageUri.startsWith('ph://') && !imageUri.startsWith('http://') && !imageUri.startsWith('https://')) {
        const localImagePath = imageUri.replace(/^file:\/\//, '');
        if (!(await fs.exists(localImagePath))) {
          Funcs.log('Skip compress unavailable image', imageUri);
          return imgData;
        }
      }

      // Compress ảnh với timeout 5s (iOS & Android)
      const newUri = await Promise.race([
        Compress.Image.compress(imageUri, { compressionMethod: 'auto', maxWidth: 1080, maxHeight: 1080, quality: 0.9 }),
        new Promise((_, reject) => setTimeout(() => reject('Compress timeout'), 5000))
      ]);
      if (newUri && typeof newUri === 'string' && newUri.length > 0) imgData.uri = newUri;
    } catch (err) {
      Funcs.log('Optimize image error', err);
    }
    return imgData;
  }

  // Xử lý data object ko fải file
  const validFields = ['image', 'images', 'audio', 'file', 'icon', 'avatar', 'bankQR'];
  const keys = Object.keys(params);
  for (let i = 0; i < keys.length; i += 1) {
    const key = keys[i];
    if ((typeof params[key] === 'object' || typeof params[key] === 'function') && validFields.indexOf(key) < 0) {
      if (moment.isDate(params[key])) {
        params[key] = moment(params[key])
          .toDate()
          .toISOString();
      } else {
        Funcs.log('POST_FORM update data', params[key]);
        params[key] = JSON.stringify(params[key]);
      }
    }
  }

  // Check heic file
  let { images, image } = params;
  if (images?.length > 0) {
    for (let i = 0; i < images.length; i += 1) images[i] = await optimizeImage(images[i]);
    params = { ...params, images };
  }
  if (image) {
    image = await optimizeImage(image);
    params = { ...params, image };
  }

  // Form data
  let formData = new FormData();
  let fileCount = 0;
  Object.keys(params).forEach((key) => {
    const value = params[key];
    if (Array.isArray(value)) {
      value.forEach((val) => {
        if (val?.uri === '') return;
        if (val?.uri) fileCount += 1;
        formData.append(`${key}`, normalizeFile(val));
      });
    } else {
      if (value?.uri === '') return;
      if (value?.uri) fileCount += 1;
      formData.append(key, normalizeFile(value));
    }
  });
  let headers = genHeader(auth);
  headers = { ...headers, 'Content-Type': 'multipart/form-data', Accept: 'multipart/form-data' };
  const config = { url, params, headers, method: 'POST_FORM' };
  const isStaticUrl = isStaticURL(url);
  const requestBaseURL = isStaticUrl ? '' : instance.defaults.baseURL;
  // Chỉ nới timeout khi có file, form không có file giữ timeout mặc định của instance
  let timeout = instance.defaults.timeout;
  if (fileCount > 0) timeout = UPLOAD_TIMEOUT;
  if (headers.timeout > 0) timeout = headers.timeout;
  const startedAt = Date.now();
  try {
    let fullUrl = isStaticUrl ? url : requestBaseURL + url;
    Funcs.log('POST_FORM', fullUrl, params, headers);
    var res = await instance.post(url, formData, {
      timeout,
      headers,
      baseURL: requestBaseURL,
      withCredentials: !isStaticUrl,
      cancelToken: new axios.CancelToken(function executor(c) {
        if (cancelRequest) cancelRequest.cancel = c;
      })
    });

    // Delete converted file
    for (let i = 0; i < arrayImageConverted.length; i += 1) {
      File.deleteFile(arrayImageConverted[i]);
    }
    return parseSuccessResult(res, config);
  } catch (err) {
    logUploadTimeout(err, { url, baseURL: requestBaseURL, timeout, startedAt, fileCount });
    return parseErrorResult(err, config);
  }
}

export default {
  UPLOAD_TIMEOUT,
  get,
  post,
  postForm,
  Status,
  init: () => {
    baseURL = UrlConst.API;
    instance = axios.create({ baseURL, withCredentials: true, timeout: 15000 });
    ApiHost.bind(instance);
  }
};
